import type {
  ActivityItem,
  AdminSettingsStatus,
  AdminVaultRecord,
  BusinessCenterUserRecord,
  BusinessCenterHealthSnapshot,
  BusinessCenterIntelligencePayload,
  ClientVaultContext,
  ClientRecord,
  ClientPortalPayload,
  ClientPortalWorkspaceKey,
  DashboardStats,
  EventPersonalization,
  EventRecord,
  MemoryVaultUpdatePayload,
  MemoryVaultWorkspace,
  MediaItem,
  MediaKind,
  MediaStatus,
  PaymentSettings,
  VaultConfig,
} from "./types";
import { buildDemoBusinessCenterIntelligence } from "./dashboardIntelligence";
import { isDemoRuntimeMode, isProductionRuntimeMode } from "./runtimeMode";

export const defaultGuestUploadInstructions =
  "Please upload your favorite photos and videos from this event. All uploads will be reviewed before they appear in the Memory Media Vault.";
export const defaultApprovalNotice =
  "Your photos and videos will be reviewed before they appear in the Memory Media Vault.";
export const defaultConfirmationMessage =
  "Thank you. Your upload has been received and will be reviewed before it appears in the Memory Media Vault.";

const defaultConfig: VaultConfig = {
  publicBaseUrl: window.location.origin,
  clientSlug: "amara-thompson",
  eventSlug: "amara-50th",
  turnstileSiteKey: "",
  maxPhotoSizeMb: 25,
  maxVideoSizeMb: 500,
  maxFilesPerUpload: 20,
  discoveryCallBookingUrl: "",
  supportEmail: "support@kreatethedate.com",
  supportRequestUrl: "mailto:support@kreatethedate.com",
};

export function defaultEventPersonalization(clientSlug: string, eventSlug: string): EventPersonalization {
  const eventName = eventSlug
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || "This Event";
  return {
    clientName: clientSlug || "Client",
    clientSlug,
    eventName,
    eventSlug,
    vaultDisplayName: `${eventName} Vault`,
    eventType: "Event",
    memoryHubTitle: `${eventName} Memory Hub`,
    memoryHubIntroText: "Upload, view, and revisit approved memories from this special event.",
    uploadPageTitle: `${eventName} Vault`,
    uploadPageIntroText: defaultGuestUploadInstructions,
    guestUploadInstructions: defaultGuestUploadInstructions,
    galleryTitle: `${eventName} Memory Gallery`,
    galleryIntroText: "Approved photos and videos from this celebration will appear here after review.",
    emptyGalleryMessage:
      "No memories have been approved yet. Photos and videos will appear here once they are uploaded and approved.",
    primaryThemeColor: "#050505",
    secondaryThemeColor: "#FFF9EF",
    accentColor: "#FF6A2B",
    uploadButtonLabel: "Upload Memories",
    approvalNotice: defaultApprovalNotice,
    confirmationMessage: defaultConfirmationMessage,
    galleryButtonLabel: "View Memory Gallery",
    memoryHubButtonLabel: "Back to Memory Hub",
  };
}

type MockDataModule = typeof import("../data/mockData");

export const isStrictBackendMode = import.meta.env.VITE_KTD_STRICT_BACKEND === "true";
export const isDemoMode = (isDemoRuntimeMode() || (import.meta.env.DEV && !isProductionRuntimeMode())) && !isStrictBackendMode;

function requestError(error: unknown): never {
  throw error instanceof Error ? error : new Error("Request failed.");
}

function demoValue<T>(value: T, error: unknown): T {
  if (isDemoMode) return value;
  return requestError(error);
}

async function demoData<T>(select: (data: MockDataModule) => T, error: unknown): Promise<T> {
  if (!isDemoMode) return requestError(error);
  const data = await import("../data/mockData");
  return select(data);
}

async function placeholderData<T>(select: (data: MockDataModule) => T): Promise<T> {
  const data = await import("../data/mockData");
  return select(data);
}

function buildDemoClientVaultContext(): ClientVaultContext {
  return {
    user: {
      id: "demo-client-user",
      email: "client@example.com",
      name: "Client Primary Contact",
      role: "client",
    },
    client: {
      id: "client_demo_001",
      clientName: "Amara Thompson",
      clientEmail: "amara@example.com",
      clientSlug: "amara-thompson",
    },
    vaults: [
      {
        id: "vault_demo_001",
        clientId: "client_demo_001",
        vaultName: "Amara 50th Memory Media Vault",
        status: "active",
        projectId: "project_memory_hub_001",
        event: {
          id: "event_demo_001",
          vaultEventId: "vault_event_demo_001",
          eventName: "Amara's 50th Celebration",
          eventSlug: "amara-50th",
          eventType: "Event",
          eventDate: "2026-08-01",
          uploadStatus: "open",
          memoryHubUrl: "/memory-hub/amara-thompson/amara-50th",
          legacyUploadUrl: "/upload/amara-thompson/amara-50th",
          uploadUrl: "/upload/amara-thompson/amara-50th",
          galleryUrl: "/gallery/amara-thompson/amara-50th",
          guestUploadLinkId: "guest_link_demo_001",
          guestUploadSlug: "amara-50th",
          guestUploadLinkStatus: "active",
        },
      },
    ],
  };
}

function buildKreatethedateDemoClientPortalPayload(): ClientPortalPayload {
  const client = {
    id: "client_demo_001",
    clientName: "Amara Thompson",
    clientEmail: "amara@example.com",
    clientSlug: "amara-thompson",
    latestEventSlug: "amara-50th",
    uploadUrl: "/upload/amara-thompson/amara-50th",
    galleryUrl: "/gallery/amara-thompson/amara-50th",
    memoryHubUrl: "https://kreatethedate.com/memory-hub/amara-50th",
    cloudflareAccountOwnerEmail: "amara@example.com",
    storageSetupStatus: "connected",
    cloudflareBillingUrl: "",
    createdAt: "2026-05-30T14:00:00.000Z",
    updatedAt: "2026-05-30T14:00:00.000Z",
  } as ClientRecord;

  return {
    client,
    brand: "kreatethedate",
    portalWorkspace: "kreatethedate",
    overview: {
      proposalCount: 1,
      agreementCount: 1,
      paymentCount: 1,
      projectCount: 2,
      activityCount: 4,
      openItems: 3,
      nextStep: "Review the current proposal and continue tracking the active event delivery items.",
      lastActivityAt: "2026-05-30T14:00:00.000Z",
    },
    proposals: [
      {
        id: "proposal_demo_001",
        proposalNumber: "PR-0001",
        status: "Proposal Viewed",
        packageName: "Signature Celebration Package",
        selectedServices: "Planning, Design, Coordination",
        recommendedAddons: "Guest Concierge",
        totalCents: 480000,
        balanceDueCents: 240000,
        reviewUrl: "/proposal/demo-001",
        createdAt: "2026-05-28T14:00:00.000Z",
        updatedAt: "2026-05-30T14:00:00.000Z",
        sentAt: "2026-05-28T14:00:00.000Z",
        viewedAt: "2026-05-30T14:00:00.000Z",
        acceptedAt: null,
        declinedAt: null,
        revisionRequestedAt: null,
      },
    ],
    agreements: [
      {
        id: "agreement_demo_001",
        agreementNumber: "AG-0001",
        proposalNumber: "PR-0001",
        status: "sent",
        provider: "DocuSign placeholder",
        agreementUrl: "/agreement/demo-001",
        sentAt: "2026-05-29T14:00:00.000Z",
        viewedAt: "2026-05-30T14:00:00.000Z",
        signedAt: null,
        signerName: null,
        signerEmail: null,
        createdAt: "2026-05-29T14:00:00.000Z",
        updatedAt: "2026-05-30T14:00:00.000Z",
      },
    ],
    payments: [
      {
        id: "payment_demo_001",
        invoiceNumber: "INV-0001",
        proposalNumber: "PR-0001",
        status: "Invoice Sent",
        invoiceStatus: "sent",
        paymentMode: "deposit",
        currency: "USD",
        stripeInvoiceId: null,
        stripeInvoiceUrl: null,
        stripeCheckoutUrl: null,
        subtotalCents: 480000,
        discountCents: 0,
        depositCents: 120000,
        totalCents: 480000,
        amountPaidCents: 120000,
        amountRemainingCents: 360000,
        dueDate: "2026-06-15",
        finalDueDate: "2026-07-15",
        paidAt: "2026-05-30T14:00:00.000Z",
        createdAt: "2026-05-29T14:00:00.000Z",
        updatedAt: "2026-05-30T14:00:00.000Z",
      },
    ],
    projects: [
      {
        id: "project_demo_001",
        projectNumber: "PJ-0001",
        brand: "kreatethedate",
        companyId: client.id,
        clientId: client.id,
        projectName: "Amara 50th Celebration",
        projectType: "Event Production",
        projectStatus: "Active",
        projectPhase: "Planning",
        paymentStatus: "Partial",
        assignedServices: ["Planning", "Coordination"],
        milestones: ["Kickoff", "Design", "Delivery"],
        deliverables: ["Timeline", "Guest updates"],
        completionPercent: 42,
        revenueCents: 480000,
        portalAccessStatus: "active",
        portalAccessNotes: "Portal access confirmed for preview.",
        notes: "Demo project record for the universal client portal.",
        startDate: "2026-05-20",
        dueDate: "2026-08-01",
        updatedAt: "2026-05-30T14:00:00.000Z",
      },
      {
        id: "project_demo_002",
        projectNumber: "PJ-0002",
        brand: "kreatethedate",
        companyId: client.id,
        clientId: client.id,
        projectName: "Memory Hub Refresh",
        projectType: "Portal Update",
        projectStatus: "Review",
        projectPhase: "Approval",
        paymentStatus: "Pending",
        assignedServices: ["Content Review"],
        milestones: ["Review", "Approve"],
        deliverables: ["Updated notes"],
        completionPercent: 78,
        revenueCents: 120000,
        portalAccessStatus: "active",
        portalAccessNotes: "Preview-only project record.",
        notes: "Secondary project example for scoped portal navigation.",
        startDate: "2026-05-24",
        dueDate: "2026-06-20",
        updatedAt: "2026-05-30T14:00:00.000Z",
      },
    ],
    tasks: [
      {
        id: "task_demo_001",
        brand: "kreatethedate",
        taskNumber: "BCT-0001",
        clientId: client.id,
        clientName: client.clientName,
        projectId: "project_demo_001",
        projectName: "Amara 50th Celebration",
        taskTitle: "Confirm guest list review",
        description: "Review the latest guest list and mark any outstanding questions.",
        dueDate: "2026-06-18",
        status: "In Progress",
        priority: "High",
        assignedMember: "Team Member Placeholder",
        notes: "Demo task linked to the active client project.",
        createdAt: "2026-05-30T14:00:00.000Z",
        updatedAt: "2026-05-30T14:00:00.000Z",
      },
    ],
    documents: [
      {
        id: "doc_demo_001",
        brand: "kreatethedate",
        documentNumber: "BCD-0001",
        folderKind: "Client",
        clientId: client.id,
        clientName: client.clientName,
        projectId: "project_demo_001",
        projectName: "Amara 50th Celebration",
        documentName: "Welcome packet",
        documentType: "Proposal",
        status: "Approved",
        notes: "Preview document for the client portal.",
        createdAt: "2026-05-30T14:00:00.000Z",
        updatedAt: "2026-05-30T14:00:00.000Z",
      },
    ],
    activity: [
      {
        id: "activity_demo_001",
        brand: "kreatethedate",
        kind: "proposal",
        title: "Proposal PR-0001 · Proposal Viewed",
        detail: "Signature Celebration Package",
        relatedUrl: "/proposal/demo-001",
        createdAt: "2026-05-30T14:00:00.000Z",
      },
      {
        id: "activity_demo_002",
        brand: "kreatethedate",
        kind: "agreement",
        title: "Agreement AG-0001 · sent",
        detail: "DocuSign placeholder",
        relatedUrl: "/agreement/demo-001",
        createdAt: "2026-05-29T14:00:00.000Z",
      },
      {
        id: "activity_demo_003",
        brand: "kreatethedate",
        kind: "payment",
        title: "Invoice INV-0001 · Invoice Sent",
        detail: "$1200.00 paid of $4800.00.",
        relatedUrl: null,
        createdAt: "2026-05-30T14:00:00.000Z",
      },
      {
        id: "activity_demo_004",
        brand: "kreatethedate",
        kind: "project",
        title: "PJ-0001 · Active",
        detail: "Amara 50th Celebration",
        relatedUrl: null,
        createdAt: "2026-05-30T14:00:00.000Z",
      },
    ],
  };
}

function readDemoClientPortalWorkspacePreference(): ClientPortalWorkspaceKey {
  const allowed: ClientPortalWorkspaceKey[] = ["kreatethedate", "truevance_connect", "ar_recovery"];
  const fromWindow = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("workspace") : null;
  const fromStorage = typeof window !== "undefined" ? window.localStorage.getItem("ktd_demo_portal_workspace") : null;
  const preferred = fromWindow || fromStorage;
  return allowed.includes(preferred as ClientPortalWorkspaceKey) ? (preferred as ClientPortalWorkspaceKey) : "kreatethedate";
}

function buildDemoClientPortalPayload(workspace: ClientPortalWorkspaceKey = readDemoClientPortalWorkspacePreference()): ClientPortalPayload {
  switch (workspace) {
    case "truevance_connect":
      return buildTruevanceConnectDemoClientPortalPayload();
    case "ar_recovery":
      return buildARRecoveryDemoClientPortalPayload();
    default:
      return buildKreatethedateDemoClientPortalPayload();
  }
}

function buildTruevanceConnectDemoClientPortalPayload(): ClientPortalPayload {
  const client = {
    id: "client_demo_tv_001",
    clientName: "Northstar Strategy Group",
    clientEmail: "elena@northstarstrategy.com",
    clientSlug: "northstar-strategy",
    organizationName: "Northstar Strategy Group",
    contactName: "Elena Torres",
    contactEmail: "elena@northstarstrategy.com",
    contactPhone: "(555) 018-0044",
    clientStatus: "Active",
    portalAccessStatus: "active",
    portalAccessNotes: "Portal access confirmed for the consulting workspace.",
    futureProjectNotes: "Phase 2 strategy review and project planning in progress.",
    latestEventSlug: "q3-strategy-review",
    storageSetupStatus: "connected",
    createdAt: "2026-05-20T13:00:00.000Z",
    updatedAt: "2026-06-18T15:30:00.000Z",
  } as ClientRecord;

  return {
    client,
    brand: "truevance_connect",
    portalWorkspace: "truevance_connect",
    overview: {
      proposalCount: 1,
      agreementCount: 1,
      paymentCount: 2,
      projectCount: 2,
      activityCount: 5,
      openItems: 4,
      nextStep: "Review the latest assessment, then open the recommendation packet before the next project checkpoint.",
      lastActivityAt: "2026-06-18T15:30:00.000Z",
    },
    proposals: [
      {
        id: "proposal_tv_001",
        proposalNumber: "TV-1001",
        status: "Assessment Reviewed",
        packageName: "Operational Assessment",
        selectedServices: "Discovery, Analysis, Planning",
        recommendedAddons: "Roadmap Workshop",
        totalCents: 720000,
        balanceDueCents: 360000,
        reviewUrl: "#tasks",
        createdAt: "2026-06-15T13:00:00.000Z",
        updatedAt: "2026-06-18T15:30:00.000Z",
        sentAt: "2026-06-15T13:00:00.000Z",
        viewedAt: "2026-06-17T16:00:00.000Z",
        acceptedAt: null,
        declinedAt: null,
        revisionRequestedAt: null,
      },
    ],
    agreements: [
      {
        id: "agreement_tv_001",
        agreementNumber: "AG-2001",
        proposalNumber: "TV-1001",
        status: "sent",
        provider: "DocuSign placeholder",
        agreementUrl: "#documents",
        sentAt: "2026-06-17T16:00:00.000Z",
        viewedAt: "2026-06-18T15:30:00.000Z",
        signedAt: null,
        signerName: null,
        signerEmail: null,
        createdAt: "2026-06-17T16:00:00.000Z",
        updatedAt: "2026-06-18T15:30:00.000Z",
      },
    ],
    payments: [
      {
        id: "payment_tv_001",
        invoiceNumber: "INV-2001",
        proposalNumber: "TV-1001",
        status: "Invoice Sent",
        invoiceStatus: "sent",
        paymentMode: "deposit",
        currency: "USD",
        stripeInvoiceId: null,
        stripeInvoiceUrl: null,
        stripeCheckoutUrl: null,
        subtotalCents: 720000,
        discountCents: 0,
        depositCents: 180000,
        totalCents: 720000,
        amountPaidCents: 180000,
        amountRemainingCents: 540000,
        dueDate: "2026-06-30",
        finalDueDate: "2026-07-31",
        paidAt: "2026-06-18T15:30:00.000Z",
        createdAt: "2026-06-17T16:00:00.000Z",
        updatedAt: "2026-06-18T15:30:00.000Z",
      },
      {
        id: "payment_tv_002",
        invoiceNumber: "INV-2002",
        proposalNumber: "TV-1001",
        status: "Pending",
        invoiceStatus: "draft",
        paymentMode: "balance",
        currency: "USD",
        stripeInvoiceId: null,
        stripeInvoiceUrl: null,
        stripeCheckoutUrl: null,
        subtotalCents: 240000,
        discountCents: 0,
        depositCents: 0,
        totalCents: 240000,
        amountPaidCents: 0,
        amountRemainingCents: 240000,
        dueDate: "2026-07-31",
        finalDueDate: "2026-08-15",
        paidAt: null,
        createdAt: "2026-06-18T15:30:00.000Z",
        updatedAt: "2026-06-18T15:30:00.000Z",
      },
    ],
    projects: [
      {
        id: "project_tv_001",
        projectNumber: "TV-PJ-001",
        brand: "truevance_connect",
        companyId: client.id,
        clientId: client.id,
        projectName: "Q3 Strategy Review",
        projectType: "Advisory Project",
        projectStatus: "Active",
        projectPhase: "Planning",
        paymentStatus: "Partial",
        assignedServices: ["Assessment", "Planning"],
        milestones: ["Assessment complete", "Recommendation delivered", "Delivery kickoff"],
        deliverables: ["Assessment memo", "Recommendations packet"],
        completionPercent: 58,
        revenueCents: 720000,
        portalAccessStatus: "active",
        portalAccessNotes: "Client portal scoped to advisory delivery.",
        notes: "Consulting workspace demo project.",
        startDate: "2026-06-12",
        dueDate: "2026-07-20",
        updatedAt: "2026-06-18T15:30:00.000Z",
      },
      {
        id: "project_tv_002",
        projectNumber: "TV-PJ-002",
        brand: "truevance_connect",
        companyId: client.id,
        clientId: client.id,
        projectName: "Executive Recommendations",
        projectType: "Recommendation Project",
        projectStatus: "Review",
        projectPhase: "Approval",
        paymentStatus: "Pending",
        assignedServices: ["Recommendation", "Roadmap"],
        milestones: ["Outline", "Review", "Approval"],
        deliverables: ["Executive summary"],
        completionPercent: 76,
        revenueCents: 240000,
        portalAccessStatus: "active",
        portalAccessNotes: "Preview-only project record.",
        notes: "Recommendation and roadmap example for the shared portal.",
        startDate: "2026-06-16",
        dueDate: "2026-07-02",
        updatedAt: "2026-06-18T15:30:00.000Z",
      },
    ],
    tasks: [
      {
        id: "task_tv_001",
        brand: "truevance_connect",
        taskNumber: "TVC-0001",
        clientId: client.id,
        clientName: client.clientName,
        projectId: "project_tv_001",
        projectName: "Q3 Strategy Review",
        taskTitle: "Complete assessment review",
        description: "Review the discovery notes and mark any open questions for the next checkpoint.",
        dueDate: "2026-06-21",
        status: "In Progress",
        priority: "High",
        assignedMember: "Team Member Placeholder",
        notes: "Consulting demo task linked to the active workspace project.",
        createdAt: "2026-06-18T15:30:00.000Z",
        updatedAt: "2026-06-18T15:30:00.000Z",
      },
      {
        id: "task_tv_002",
        brand: "truevance_connect",
        taskNumber: "TVC-0002",
        clientId: client.id,
        clientName: client.clientName,
        projectId: "project_tv_002",
        projectName: "Executive Recommendations",
        taskTitle: "Prepare recommendation summary",
        description: "Compile the recommendation packet for the client-facing review session.",
        dueDate: "2026-06-25",
        status: "Waiting",
        priority: "Medium",
        assignedMember: "Manager Placeholder",
        notes: "Placeholder workflow item for the shared portal.",
        createdAt: "2026-06-18T15:30:00.000Z",
        updatedAt: "2026-06-18T15:30:00.000Z",
      },
    ],
    documents: [
      {
        id: "doc_tv_001",
        brand: "truevance_connect",
        documentNumber: "TVD-0001",
        folderKind: "Client",
        clientId: client.id,
        clientName: client.clientName,
        projectId: "project_tv_001",
        projectName: "Q3 Strategy Review",
        documentName: "Assessment memo",
        documentType: "Brief",
        status: "In Review",
        notes: "Consulting workspace document preview.",
        createdAt: "2026-06-18T15:30:00.000Z",
        updatedAt: "2026-06-18T15:30:00.000Z",
      },
      {
        id: "doc_tv_002",
        brand: "truevance_connect",
        documentNumber: "TVD-0002",
        folderKind: "Project",
        clientId: client.id,
        clientName: client.clientName,
        projectId: "project_tv_002",
        projectName: "Executive Recommendations",
        documentName: "Recommendations packet",
        documentType: "Proposal",
        status: "Approved",
        notes: "Ready for client review in the shared portal.",
        createdAt: "2026-06-18T15:30:00.000Z",
        updatedAt: "2026-06-18T15:30:00.000Z",
      },
    ],
    activity: [
      {
        id: "activity_tv_001",
        brand: "truevance_connect",
        kind: "proposal",
        title: "TV-1001 - Assessment Reviewed",
        detail: "Operational assessment packet was reviewed by the client.",
        relatedUrl: "#tasks",
        createdAt: "2026-06-18T15:30:00.000Z",
      },
      {
        id: "activity_tv_002",
        brand: "truevance_connect",
        kind: "agreement",
        title: "AG-2001 - Sent",
        detail: "Agreement placeholder was issued for signature.",
        relatedUrl: "#documents",
        createdAt: "2026-06-17T16:00:00.000Z",
      },
      {
        id: "activity_tv_003",
        brand: "truevance_connect",
        kind: "payment",
        title: "INV-2001 - Invoice Sent",
        detail: "$1800.00 paid of $7200.00.",
        relatedUrl: "#billing",
        createdAt: "2026-06-18T15:30:00.000Z",
      },
      {
        id: "activity_tv_004",
        brand: "truevance_connect",
        kind: "project",
        title: "TV-PJ-001 - Active",
        detail: "Q3 Strategy Review is in planning.",
        relatedUrl: "#projects",
        createdAt: "2026-06-18T15:30:00.000Z",
      },
      {
        id: "activity_tv_005",
        brand: "truevance_connect",
        kind: "file",
        title: "Assessment memo uploaded",
        detail: "Consulting deliverable is ready for client review.",
        relatedUrl: "#documents",
        createdAt: "2026-06-18T15:30:00.000Z",
      },
    ],
  };
}

function buildARRecoveryDemoClientPortalPayload(): ClientPortalPayload {
  const client = {
    id: "client_demo_ar_001",
    clientName: "Aster Media Group",
    clientEmail: "ap@astermediagroup.com",
    clientSlug: "aster-media-group",
    organizationName: "Aster Media Group",
    contactName: "Jordan Lee",
    contactEmail: "jordan@astermediagroup.com",
    contactPhone: "(555) 019-3321",
    clientStatus: "Active",
    portalAccessStatus: "active",
    portalAccessNotes: "Recovery workspace access confirmed for company-scoped records.",
    futureProjectNotes: "Ongoing recovery review and follow-up queue remain active.",
    latestEventSlug: "recovery-dashboard",
    storageSetupStatus: "connected",
    createdAt: "2026-05-18T13:00:00.000Z",
    updatedAt: "2026-06-18T16:00:00.000Z",
  } as ClientRecord;

  return {
    client,
    brand: "ar_recovery",
    portalWorkspace: "ar_recovery",
    overview: {
      proposalCount: 1,
      agreementCount: 1,
      paymentCount: 2,
      projectCount: 3,
      activityCount: 6,
      openItems: 7,
      nextStep: "Review today's promise-to-pay queue, then open the customer accounts requiring action.",
      lastActivityAt: "2026-06-18T16:00:00.000Z",
    },
    proposals: [
      {
        id: "statement_ar_001",
        proposalNumber: "ST-1001",
        status: "Sent",
        packageName: "Open AR Statement",
        selectedServices: "Current, 1-30, 31-60",
        recommendedAddons: "Collection letter follow-up",
        totalCents: 3245000,
        balanceDueCents: 1245000,
        reviewUrl: "#billing",
        createdAt: "2026-06-16T13:00:00.000Z",
        updatedAt: "2026-06-18T16:00:00.000Z",
        sentAt: "2026-06-16T13:00:00.000Z",
        viewedAt: "2026-06-18T10:00:00.000Z",
        acceptedAt: null,
        declinedAt: null,
        revisionRequestedAt: null,
      },
    ],
    agreements: [
      {
        id: "letter_ar_001",
        agreementNumber: "CL-1001",
        proposalNumber: "ST-1001",
        status: "sent",
        provider: "Letter template",
        agreementUrl: "#documents",
        sentAt: "2026-06-17T13:00:00.000Z",
        viewedAt: "2026-06-18T10:00:00.000Z",
        signedAt: null,
        signerName: null,
        signerEmail: null,
        createdAt: "2026-06-17T13:00:00.000Z",
        updatedAt: "2026-06-18T16:00:00.000Z",
      },
    ],
    payments: [
      {
        id: "invoice_ar_001",
        invoiceNumber: "INV-4001",
        proposalNumber: "ST-1001",
        status: "Invoice Sent",
        invoiceStatus: "sent",
        paymentMode: "collection",
        currency: "USD",
        stripeInvoiceId: null,
        stripeInvoiceUrl: null,
        stripeCheckoutUrl: null,
        subtotalCents: 3245000,
        discountCents: 0,
        depositCents: 0,
        totalCents: 3245000,
        amountPaidCents: 1025000,
        amountRemainingCents: 2220000,
        dueDate: "2026-06-21",
        finalDueDate: "2026-07-01",
        paidAt: "2026-06-18T16:00:00.000Z",
        createdAt: "2026-06-17T13:00:00.000Z",
        updatedAt: "2026-06-18T16:00:00.000Z",
      },
      {
        id: "invoice_ar_002",
        invoiceNumber: "INV-4002",
        proposalNumber: "ST-1001",
        status: "Pending",
        invoiceStatus: "draft",
        paymentMode: "collection",
        currency: "USD",
        stripeInvoiceId: null,
        stripeInvoiceUrl: null,
        stripeCheckoutUrl: null,
        subtotalCents: 845000,
        discountCents: 0,
        depositCents: 0,
        totalCents: 845000,
        amountPaidCents: 0,
        amountRemainingCents: 845000,
        dueDate: "2026-06-24",
        finalDueDate: "2026-07-08",
        paidAt: null,
        createdAt: "2026-06-18T16:00:00.000Z",
        updatedAt: "2026-06-18T16:00:00.000Z",
      },
    ],
    projects: [
      {
        id: "ar_account_001",
        projectNumber: "AR-1001",
        brand: "ar_recovery",
        companyId: client.id,
        clientId: client.id,
        projectName: "Aster Media Group",
        projectType: "Recovery Account",
        projectStatus: "Escalated",
        projectPhase: "Collections",
        paymentStatus: "Past Due",
        assignedServices: ["Collections", "Escalation"],
        milestones: ["Statement sent", "Promise-to-pay due", "Escalation review"],
        deliverables: ["Collection letter", "Account review"],
        completionPercent: 42,
        revenueCents: 3245000,
        portalAccessStatus: "active",
        portalAccessNotes: "Company-scoped recovery account view.",
        notes: "Primary customer account in the recovery workspace.",
        startDate: "2026-06-01",
        dueDate: "2026-06-30",
        updatedAt: "2026-06-18T16:00:00.000Z",
      },
      {
        id: "ar_account_002",
        projectNumber: "AR-1002",
        brand: "ar_recovery",
        companyId: client.id,
        clientId: client.id,
        projectName: "Northwind Supplies",
        projectType: "Recovery Account",
        projectStatus: "At Risk",
        projectPhase: "Follow-up",
        paymentStatus: "Past Due",
        assignedServices: ["Follow-up", "Reminder"],
        milestones: ["Reminder sent", "Callback pending"],
        deliverables: ["Promise review"],
        completionPercent: 63,
        revenueCents: 845000,
        portalAccessStatus: "active",
        portalAccessNotes: "Follow-up queue example for the AR portal.",
        notes: "Second customer account in the recovery workspace.",
        startDate: "2026-06-05",
        dueDate: "2026-06-24",
        updatedAt: "2026-06-18T16:00:00.000Z",
      },
      {
        id: "ar_account_003",
        projectNumber: "AR-1003",
        brand: "ar_recovery",
        companyId: client.id,
        clientId: client.id,
        projectName: "Crescent Interiors",
        projectType: "Recovery Account",
        projectStatus: "Current",
        projectPhase: "Monitoring",
        paymentStatus: "Current",
        assignedServices: ["Monitoring"],
        milestones: ["Invoice current", "Monitor terms"],
        deliverables: ["Account summary"],
        completionPercent: 90,
        revenueCents: 295000,
        portalAccessStatus: "active",
        portalAccessNotes: "Low-risk account kept visible for early action.",
        notes: "Current account example for the recovery dashboard.",
        startDate: "2026-06-14",
        dueDate: "2026-07-01",
        updatedAt: "2026-06-18T16:00:00.000Z",
      },
    ],
    tasks: [
      {
        id: "tickler_ar_001",
        brand: "ar_recovery",
        taskNumber: "AR-TK-0001",
        clientId: client.id,
        clientName: client.clientName,
        projectId: "ar_account_001",
        projectName: "Aster Media Group",
        taskTitle: "Promise-to-pay follow-up",
        description: "Call the customer after the promise date and confirm remittance status.",
        dueDate: "2026-06-19",
        status: "In Progress",
        priority: "Urgent",
        assignedMember: "Collector Placeholder",
        notes: "High-priority recovery follow-up item.",
        createdAt: "2026-06-18T16:00:00.000Z",
        updatedAt: "2026-06-18T16:00:00.000Z",
      },
      {
        id: "tickler_ar_002",
        brand: "ar_recovery",
        taskNumber: "AR-TK-0002",
        clientId: client.id,
        clientName: client.clientName,
        projectId: "ar_account_002",
        projectName: "Northwind Supplies",
        taskTitle: "Escalation reminder",
        description: "Prepare director review notes for the overdue account queue.",
        dueDate: "2026-06-20",
        status: "Waiting",
        priority: "High",
        assignedMember: "Manager Placeholder",
        notes: "Escalation queue item for the recovery command center.",
        createdAt: "2026-06-18T16:00:00.000Z",
        updatedAt: "2026-06-18T16:00:00.000Z",
      },
    ],
    documents: [
      {
        id: "doc_ar_001",
        brand: "ar_recovery",
        documentNumber: "ARD-0001",
        folderKind: "Client",
        clientId: client.id,
        clientName: client.clientName,
        projectId: "ar_account_001",
        projectName: "Aster Media Group",
        documentName: "Collection letter 1",
        documentType: "Reference",
        status: "Approved",
        notes: "Recovery correspondence example.",
        createdAt: "2026-06-17T13:00:00.000Z",
        updatedAt: "2026-06-18T16:00:00.000Z",
      },
      {
        id: "doc_ar_002",
        brand: "ar_recovery",
        documentNumber: "ARD-0002",
        folderKind: "Project",
        clientId: client.id,
        clientName: client.clientName,
        projectId: "ar_account_002",
        projectName: "Northwind Supplies",
        documentName: "Statement package",
        documentType: "Invoice",
        status: "In Review",
        notes: "Statement preview for the recovery portal.",
        createdAt: "2026-06-18T16:00:00.000Z",
        updatedAt: "2026-06-18T16:00:00.000Z",
      },
    ],
    activity: [
      {
        id: "activity_ar_001",
        brand: "ar_recovery",
        kind: "proposal",
        title: "ST-1001 - Statement Sent",
        detail: "Open AR statement issued to the customer.",
        relatedUrl: "#billing",
        createdAt: "2026-06-16T13:00:00.000Z",
      },
      {
        id: "activity_ar_002",
        brand: "ar_recovery",
        kind: "agreement",
        title: "CL-1001 - Collection letter sent",
        detail: "Recovery correspondence entered into the queue.",
        relatedUrl: "#documents",
        createdAt: "2026-06-17T13:00:00.000Z",
      },
      {
        id: "activity_ar_003",
        brand: "ar_recovery",
        kind: "payment",
        title: "INV-4001 - Invoice Sent",
        detail: "$10250.00 paid of $32450.00.",
        relatedUrl: "#billing",
        createdAt: "2026-06-18T16:00:00.000Z",
      },
      {
        id: "activity_ar_004",
        brand: "ar_recovery",
        kind: "project",
        title: "AR-1001 - Escalated",
        detail: "Customer account moved into the escalation queue.",
        relatedUrl: "#projects",
        createdAt: "2026-06-18T16:00:00.000Z",
      },
      {
        id: "activity_ar_005",
        brand: "ar_recovery",
        kind: "message",
        title: "Promise-to-pay reminder queued",
        detail: "Collector reminder scheduled for the active account.",
        relatedUrl: "#tasks",
        createdAt: "2026-06-18T16:00:00.000Z",
      },
      {
        id: "activity_ar_006",
        brand: "ar_recovery",
        kind: "file",
        title: "Statement package uploaded",
        detail: "Recovery report material is ready for review.",
        relatedUrl: "#documents",
        createdAt: "2026-06-18T16:00:00.000Z",
      },
    ],
  };
}

export async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    headers: init?.body instanceof FormData ? undefined : { "Content-Type": "application/json" },
    ...init,
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed with ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function getConfig(): Promise<VaultConfig> {
  try {
    return await requestJson<VaultConfig>("/api/config");
  } catch (error) {
    return demoValue(defaultConfig, error);
  }
}

export async function getJourneyProposal(token: string) {
  return requestJson<{ proposal: import("./types").JourneyProposal; lineItems: import("./types").JourneyProposalItem[] }>(`/api/proposals/${token}`);
}

export async function submitProposalAction(
  token: string,
  payload: { action: "accept" | "request_changes" | "decline" | "view"; comments?: string },
) {
  return requestJson<{ ok: true; status: string; agreementUrl?: string }>(`/api/proposals/${token}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getJourneyAgreement(token: string) {
  return requestJson<{ agreement: import("./types").JourneyAgreement; proposal: import("./types").JourneyProposal; lineItems: import("./types").JourneyProposalItem[] }>(`/api/agreements/${token}`);
}

export async function submitAgreementAction(
  token: string,
  payload: { action: "view" | "sign"; signerName?: string; signerEmail?: string },
) {
  return requestJson<{ ok: true; status: string; paymentUrl?: string; paymentId?: string }>(`/api/agreements/${token}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getJourneyPayment(token: string) {
  return requestJson<{ payment: import("./types").JourneyPayment; proposal: import("./types").JourneyProposal; lineItems: import("./types").JourneyProposalItem[] }>(`/api/payments/${token}`);
}

export async function submitPaymentAction(token: string, payload: { action: "view" | "mark_received"; paidCents?: number }) {
  return requestJson<{ ok: true; status: string; onboardingUrl?: string }>(`/api/payments/${token}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getAdminJourneyDashboard() {
  return requestJson<{ journeys: import("./types").JourneySummary[] }>("/api/admin/journey-dashboard");
}

export async function getAdminProposals() {
  return requestJson<{
    journeys: import("./types").JourneySummary[];
    proposals: import("./types").AdminProposalRecord[];
  }>("/api/admin/proposals");
}

export async function getAdminAgreements() {
  return requestJson<{
    agreements: import("./types").AdminAgreementRecord[];
    proposals: import("./types").AdminProposalRecord[];
  }>("/api/admin/agreements");
}

export async function createAdminAgreement(payload: {
  proposalId: string;
  agreementUrl?: string;
}) {
  return requestJson<{ agreement: import("./types").AdminAgreementRecord }>("/api/admin/agreements", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getAdminAgreement(agreementId: string) {
  return requestJson<import("./types").AdminAgreementDetail>(`/api/admin/agreements/${agreementId}`);
}

export async function updateAdminAgreement(
  agreementId: string,
  payload: { action: "viewed" | "signed"; signerName?: string; signerEmail?: string },
) {
  return requestJson<{ agreement: import("./types").AdminAgreementRecord }>(`/api/admin/agreements/${agreementId}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getAdminPayments() {
  return requestJson<{
    payments: import("./types").AdminPaymentRecord[];
    proposals: import("./types").AdminProposalRecord[];
  }>("/api/admin/payments");
}

export async function createAdminPayment(payload: {
  proposalId: string;
  agreementId?: string | null;
  mode?: "deposit" | "in_full" | "custom";
  dueDate?: string | null;
  finalDueDate?: string | null;
}) {
  return requestJson<{ payment: import("./types").AdminPaymentRecord }>("/api/admin/payments", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getAdminPayment(paymentId: string) {
  return requestJson<import("./types").AdminPaymentDetail>(`/api/admin/payments/${paymentId}`);
}

export async function updateAdminPayment(
  paymentId: string,
  payload: { action: "invoice_sent" | "partial_received" | "paid_in_full" | "failed" | "refunded"; paidCents?: number; refundedCents?: number; reason?: string },
) {
  return requestJson<{ payment: import("./types").AdminPaymentRecord }>(`/api/admin/payments/${paymentId}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function createAdminProposal(payload: {
  action?: "save_draft" | "ready_to_send" | "send";
  leadId: string;
  clientId?: string;
  packageName?: string;
  recommendedServices?: string;
  selectedServices?: string;
  recommendedAddons?: string;
  notes?: string;
  expirationDate?: string;
  subtotalCents?: number;
  discountCents?: number;
  depositCents?: number;
  totalCents?: number;
  lineItems?: Array<{
    name?: string;
    description?: string;
    quantity?: number;
    unitCents?: number;
    lineType?: string;
  }>;
}) {
  return requestJson<{ proposal: import("./types").JourneyProposal; lineItems: import("./types").JourneyProposalItem[] }>("/api/admin/proposals", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getAdminLeads() {
  return requestJson<{
    leads: Array<{
      id: string;
      leadId: string;
      source: string;
      status: string;
      firstName: string;
      lastName: string;
      fullName: string;
      email: string;
      phone: string;
      eventType: string;
      eventDate: string;
      serviceInterest: string;
      budget: string;
      timeline: string;
      notes: string;
      referralSource: string;
      marketingSource: string;
      activityNotes: string;
      discoveryEmailSentAt: string | null;
      journeyStatus?: string;
      proposalStatus?: string;
      agreementStatus?: string;
      paymentStatus?: string;
      projectStatus?: string;
      nextStep?: string;
      journeyLeadName?: string;
      createdAt: string;
      updatedAt: string;
    }>;
  }>("/api/admin/leads");
}

export async function createAdminLead(payload: {
  fullName?: string;
  email?: string;
  phone?: string;
  eventType?: string;
  eventDate?: string;
  source?: string;
  serviceInterest?: string;
  notes?: string;
  budget?: string;
  timeline?: string;
  referralSource?: string;
  marketingSource?: string;
  estimatedValue?: string;
  isDraft?: boolean;
  activityNote?: string;
}) {
  return requestJson<{ lead: import("./types").JourneySummary }>("/api/admin/leads", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getPaymentSettings() {
  return requestJson<{ settings: PaymentSettings }>("/api/admin/payment-settings");
}

export async function updatePaymentSettings(payload: Partial<PaymentSettings>) {
  return requestJson<{ settings: PaymentSettings }>("/api/admin/payment-settings", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function getEventPersonalization(clientSlug: string, eventSlug: string): Promise<EventPersonalization> {
  try {
    const data = await requestJson<{ event: EventPersonalization }>(`/api/event/${clientSlug}/${eventSlug}`);
    return data.event;
  } catch (error) {
    return demoValue(defaultEventPersonalization(clientSlug, eventSlug), error);
  }
}

export async function getGuestUploadLink(uploadSlug: string): Promise<{
  link: {
    id: string;
    uploadSlug: string;
    status: string;
    instructions?: string;
    clientId: string;
    eventId: string;
    vaultId: string;
    vaultEventId?: string;
    clientSlug: string;
    eventSlug: string;
  };
  event: EventPersonalization;
}> {
  return requestJson(`/api/guest-upload-link/${uploadSlug}`);
}

export async function getDashboardStats(params?: {
  clientId?: string;
  eventId?: string;
  vaultId?: string;
  vaultEventId?: string;
}): Promise<DashboardStats> {
  const searchParams = new URLSearchParams();
  if (params?.clientId) searchParams.set("clientId", params.clientId);
  if (params?.eventId) searchParams.set("eventId", params.eventId);
  if (params?.vaultId) searchParams.set("vaultId", params.vaultId);
  if (params?.vaultEventId) searchParams.set("vaultEventId", params.vaultEventId);
  try {
    return await requestJson<DashboardStats>(`/api/client/dashboard?${searchParams.toString()}`);
  } catch (error) {
    return demoData((data) => data.dashboardStats, error);
  }
}

export async function getClientVaultContext(clientId?: string): Promise<ClientVaultContext> {
  const searchParams = new URLSearchParams();
  if (clientId) searchParams.set("clientId", clientId);
  try {
    return await requestJson<ClientVaultContext>(`/api/client/context?${searchParams.toString()}`);
  } catch (error) {
    return demoValue(buildDemoClientVaultContext(), error);
  }
}

export async function getClientPortal(): Promise<import("./types").ClientPortalPayload> {
  try {
    return await requestJson<import("./types").ClientPortalPayload>("/api/client/portal");
  } catch (error) {
    return demoValue(buildDemoClientPortalPayload(), error);
  }
}

export async function getMedia(params: {
  status?: MediaStatus;
  featured?: boolean;
  type?: MediaKind | "all";
  search?: string;
  clientId?: string;
  eventId?: string;
  vaultId?: string;
  vaultEventId?: string;
}): Promise<MediaItem[]> {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set("status", params.status);
  if (params.featured !== undefined) searchParams.set("featured", params.featured ? "true" : "false");
  if (params.type && params.type !== "all") searchParams.set("type", params.type);
  if (params.search) searchParams.set("search", params.search);
  if (params.clientId) searchParams.set("clientId", params.clientId);
  if (params.eventId) searchParams.set("eventId", params.eventId);
  if (params.vaultId) searchParams.set("vaultId", params.vaultId);
  if (params.vaultEventId) searchParams.set("vaultEventId", params.vaultEventId);

  try {
    const data = await requestJson<{ media: MediaItem[] }>(`/api/media?${searchParams.toString()}`);
    return data.media;
  } catch (error) {
    return demoData((data) => data.demoMedia.filter((item) => {
      if (params.status && item.status !== params.status) return false;
      if (params.featured !== undefined && item.featured !== params.featured) return false;
      if (params.type && params.type !== "all" && item.fileType !== params.type) return false;
      if (params.search && !item.uploaderName.toLowerCase().includes(params.search.toLowerCase())) return false;
      return true;
    }), error);
  }
}

export async function updateMediaAction(id: string, action: string, options?: { adminNotes?: string }) {
  const method = action === "delete" ? "DELETE" : "POST";
  const url = action === "delete" ? `/api/media/${id}` : `/api/media/${id}/${action}`;
  return requestJson<{ media: MediaItem }>(url, {
    method,
    body: options?.adminNotes ? JSON.stringify({ adminNotes: options.adminNotes }) : undefined,
  });
}

export async function getGallery(clientSlug: string, eventSlug: string, cursor?: string) {
  const searchParams = new URLSearchParams();
  if (cursor) searchParams.set("cursor", cursor);
  try {
    return await requestJson<{ media: MediaItem[]; nextCursor?: string }>(
      `/api/gallery/${clientSlug}/${eventSlug}?${searchParams.toString()}`,
    );
  } catch (error) {
    return demoData(
      (data) => ({ media: data.demoMedia.filter((item) => item.status === "approved"), nextCursor: undefined }),
      error,
    );
  }
}

export async function getFeatured(clientSlug: string, eventSlug: string) {
  try {
    return await requestJson<{ media: MediaItem[] }>(`/api/featured/${clientSlug}/${eventSlug}`);
  } catch (error) {
    return demoData(
      (data) => ({ media: data.demoMedia.filter((item) => item.status === "approved" && item.featured) }),
      error,
    );
  }
}

export async function getActivity(): Promise<ActivityItem[]> {
  try {
    const data = await requestJson<{ activity: ActivityItem[] }>("/api/activity-log");
    return data.activity;
  } catch (error) {
    return demoData((data) => data.activityItems, error);
  }
}

type BusinessCenterIntelligenceQuery = {
  companyId?: string;
  workspaceId?: string;
  clientId?: string;
  productKey?: string;
};

function buildIntelligenceSearchParams(params?: BusinessCenterIntelligenceQuery) {
  const searchParams = new URLSearchParams();
  if (params?.companyId) searchParams.set("companyId", params.companyId);
  if (params?.workspaceId) searchParams.set("workspaceId", params.workspaceId);
  if (params?.clientId) searchParams.set("clientId", params.clientId);
  if (params?.productKey) searchParams.set("productKey", params.productKey);
  return searchParams;
}

async function fetchBusinessCenterIntelligence(
  path: string,
  params: BusinessCenterIntelligenceQuery | undefined,
  fallbackVariant: "admin" | "client",
): Promise<BusinessCenterIntelligencePayload> {
  const searchParams = buildIntelligenceSearchParams(params);
  try {
    return await requestJson<BusinessCenterIntelligencePayload>(`${path}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`);
  } catch (error) {
    return demoValue(
      buildDemoBusinessCenterIntelligence(
        {
          companyId: params?.companyId || null,
          workspaceId: params?.workspaceId || null,
          clientId: params?.clientId || null,
          productKey: params?.productKey || null,
        },
        fallbackVariant,
      ),
      error,
    );
  }
}

export async function getAdminDashboardIntelligence(params?: BusinessCenterIntelligenceQuery) {
  return fetchBusinessCenterIntelligence("/api/admin/dashboard/intelligence", params, "admin");
}

export async function getAdminDashboardHealth(params?: BusinessCenterIntelligenceQuery) {
  const searchParams = buildIntelligenceSearchParams(params);
  try {
    return await requestJson<{ health: BusinessCenterHealthSnapshot }>(
      `/api/admin/dashboard/health${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
    );
  } catch (error) {
    return demoValue({ health: buildDemoBusinessCenterIntelligence({
      companyId: params?.companyId || null,
      workspaceId: params?.workspaceId || null,
      clientId: params?.clientId || null,
      productKey: params?.productKey || null,
    }, "admin").health }, error);
  }
}

export async function getAdminDashboardRecommendations(params?: BusinessCenterIntelligenceQuery) {
  const searchParams = buildIntelligenceSearchParams(params);
  try {
    return await requestJson<{ recommendations: BusinessCenterIntelligencePayload["recommendations"] }>(
      `/api/admin/dashboard/recommendations${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
    );
  } catch (error) {
    return demoValue(
      { recommendations: buildDemoBusinessCenterIntelligence({
        companyId: params?.companyId || null,
        workspaceId: params?.workspaceId || null,
        clientId: params?.clientId || null,
        productKey: params?.productKey || null,
      }, "admin").recommendations },
      error,
    );
  }
}

export async function getAdminDashboardMarketplace(params?: BusinessCenterIntelligenceQuery) {
  const searchParams = buildIntelligenceSearchParams(params);
  try {
    return await requestJson<{ marketplace: BusinessCenterIntelligencePayload["marketplace"] }>(
      `/api/admin/dashboard/marketplace${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
    );
  } catch (error) {
    return demoValue(
      { marketplace: buildDemoBusinessCenterIntelligence({
        companyId: params?.companyId || null,
        workspaceId: params?.workspaceId || null,
        clientId: params?.clientId || null,
        productKey: params?.productKey || null,
      }, "admin").marketplace },
      error,
    );
  }
}

export async function getClientDashboardIntelligence(params?: BusinessCenterIntelligenceQuery) {
  return fetchBusinessCenterIntelligence("/api/client/dashboard/intelligence", params, "client");
}

export async function getClientDashboardHealth(params?: BusinessCenterIntelligenceQuery) {
  const searchParams = buildIntelligenceSearchParams(params);
  try {
    return await requestJson<{ health: BusinessCenterHealthSnapshot }>(
      `/api/client/dashboard/health${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
    );
  } catch (error) {
    return demoValue({ health: buildDemoBusinessCenterIntelligence({
      companyId: params?.companyId || null,
      workspaceId: params?.workspaceId || null,
      clientId: params?.clientId || null,
      productKey: params?.productKey || null,
    }, "client").health }, error);
  }
}

export async function getClientDashboardRecommendations(params?: BusinessCenterIntelligenceQuery) {
  const searchParams = buildIntelligenceSearchParams(params);
  try {
    return await requestJson<{ recommendations: BusinessCenterIntelligencePayload["recommendations"] }>(
      `/api/client/dashboard/recommendations${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
    );
  } catch (error) {
    return demoValue(
      { recommendations: buildDemoBusinessCenterIntelligence({
        companyId: params?.companyId || null,
        workspaceId: params?.workspaceId || null,
        clientId: params?.clientId || null,
        productKey: params?.productKey || null,
      }, "client").recommendations },
      error,
    );
  }
}

export async function getClientDashboardMarketplace(params?: BusinessCenterIntelligenceQuery) {
  const searchParams = buildIntelligenceSearchParams(params);
  try {
    return await requestJson<{ marketplace: BusinessCenterIntelligencePayload["marketplace"] }>(
      `/api/client/dashboard/marketplace${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
    );
  } catch (error) {
    return demoValue(
      { marketplace: buildDemoBusinessCenterIntelligence({
        companyId: params?.companyId || null,
        workspaceId: params?.workspaceId || null,
        clientId: params?.clientId || null,
        productKey: params?.productKey || null,
      }, "client").marketplace },
      error,
    );
  }
}

export async function getStorageBilling() {
  try {
    return await requestJson<{
      billingUrl?: string;
      fallbackMessage: string;
      supportRequestUrl: string;
      supportEmail: string;
    }>("/api/client/storage-billing");
  } catch (error) {
    return demoValue({
      billingUrl: "",
      fallbackMessage: "Please log into your Cloudflare account and go to Billing to manage your payment method, invoices, and storage account.",
      supportRequestUrl: "mailto:support@kreatethedate.com",
      supportEmail: "support@kreatethedate.com",
    }, error);
  }
}

export async function getMemoryVaultWorkspace(params?: { clientId?: string; eventId?: string; vaultId?: string }): Promise<MemoryVaultWorkspace> {
  const searchParams = new URLSearchParams();
  if (params?.clientId) searchParams.set("clientId", params.clientId);
  if (params?.eventId) searchParams.set("eventId", params.eventId);
  if (params?.vaultId) searchParams.set("vaultId", params.vaultId);
  try {
    const data = await requestJson<{ vault: MemoryVaultWorkspace }>(`/api/memory-vault?${searchParams.toString()}`);
    return data.vault;
  } catch {
    return placeholderData((data) => data.memoryVaultWorkspace);
  }
}

export async function updateMemoryVaultWorkspace(payload: MemoryVaultUpdatePayload): Promise<MemoryVaultWorkspace> {
  try {
    const data = await requestJson<{ vault: MemoryVaultWorkspace }>("/api/memory-vault", {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    return data.vault;
  } catch (error) {
    return requestError(error);
  }
}

export async function getAdminClients(options?: { brand?: "kreatethedate" | "truevance_connect" | string }): Promise<ClientRecord[]> {
  try {
    const searchParams = new URLSearchParams();
    if (options?.brand) searchParams.set("brand", options.brand);
    const query = searchParams.toString();
    const data = await requestJson<{ clients: ClientRecord[] }>(`/api/admin/clients${query ? `?${query}` : ""}`);
    return data.clients;
  } catch (error) {
    return demoData((data) => data.adminClients, error);
  }
}

export async function getAdminClient(clientId: string): Promise<import("./types").BusinessCenterClientDetail> {
  return requestJson<import("./types").BusinessCenterClientDetail>(`/api/admin/client/${clientId}`);
}

export async function getAdminVaults(): Promise<AdminVaultRecord[]> {
  const data = await requestJson<{ vaults: AdminVaultRecord[] }>("/api/admin/vaults");
  return data.vaults;
}

export async function getAdminSettings(): Promise<AdminSettingsStatus> {
  return requestJson<AdminSettingsStatus>("/api/admin/settings");
}

export async function createAdminClient(payload: {
  brand?: string;
  clientName: string;
  clientEmail: string;
  clientSlug: string;
  organizationName?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  clientStatus?: string;
  clientNotes?: string;
  portalAccessStatus?: string;
  portalAccessNotes?: string;
  futureProjectNotes?: string;
  cloudflareAccountOwnerEmail?: string;
  cloudflareBillingUrl?: string;
}) {
  return requestJson<{ client: ClientRecord }>("/api/admin/clients", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateAdminClient(
  clientId: string,
  payload: {
    clientName?: string;
    clientEmail?: string;
    organizationName?: string;
    contactName?: string;
    contactEmail?: string;
    contactPhone?: string;
    clientStatus?: string;
    clientNotes?: string;
    portalAccessStatus?: string;
    portalAccessNotes?: string;
    futureProjectNotes?: string;
    cloudflareAccountOwnerEmail?: string;
    storageSetupStatus?: string;
    cloudflareBillingUrl?: string;
  },
) {
  return requestJson<{ client: ClientRecord }>(`/api/admin/client/${clientId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function getBusinessCenterProjects(options?: { brand?: "kreatethedate" | "truevance_connect" | string; query?: string }) {
  const searchParams = new URLSearchParams();
  if (options?.brand) searchParams.set("brand", options.brand);
  if (options?.query) searchParams.set("query", options.query);
  const query = searchParams.toString();
  return requestJson<import("./types").BusinessCenterProjectWorkspace>(`/api/admin/business-center/projects${query ? `?${query}` : ""}`);
}

export async function getBusinessCenterProject(projectId: string) {
  return requestJson<import("./types").BusinessCenterProjectDetail>(`/api/admin/business-center/projects/${projectId}`);
}

export async function createBusinessCenterProject(payload: {
  brand?: string;
  clientId?: string | null;
  clientBrand?: string;
  clientName?: string;
  projectName?: string;
  projectType?: string;
  sourceType?: string;
  sourceId?: string | null;
  sourceNumber?: string | null;
  proposalId?: string | null;
  proposalNumber?: string | null;
  agreementId?: string | null;
  agreementNumber?: string | null;
  paymentId?: string | null;
  paymentNumber?: string | null;
  projectStatus?: string;
  projectPhase?: string;
  paymentStatus?: string;
  assignedTeam?: string;
  assignedServices?: string[];
  milestones?: string[];
  deliverables?: string[];
  completionPercent?: number;
  revenueCents?: number;
  startDate?: string | null;
  dueDate?: string | null;
  portalAccessStatus?: string;
  portalAccessNotes?: string;
  notes?: string;
  leadId?: string | null;
  opportunityId?: string | null;
}) {
  return requestJson<{ project: import("./types").BusinessCenterProjectDetail }>("/api/admin/business-center/projects", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateBusinessCenterProject(
  projectId: string,
  payload: {
    clientId?: string | null;
    clientBrand?: string;
    clientName?: string;
    projectName?: string;
    projectType?: string;
    projectStatus?: string;
    projectPhase?: string;
    paymentStatus?: string;
    assignedTeam?: string;
    assignedServices?: string[];
    milestones?: string[];
    deliverables?: string[];
    completionPercent?: number;
    revenueCents?: number;
    startDate?: string | null;
    dueDate?: string | null;
    portalAccessStatus?: string;
    portalAccessNotes?: string;
    notes?: string;
    activityNote?: string;
  },
) {
  return requestJson<import("./types").BusinessCenterProjectDetail>(`/api/admin/business-center/projects/${projectId}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function archiveBusinessCenterProject(projectId: string) {
  return requestJson<import("./types").BusinessCenterProjectDetail>(`/api/admin/business-center/projects/${projectId}`, {
    method: "DELETE",
  });
}

export async function getBusinessCenterWorkspaces() {
  return requestJson<{
    workspaces: import("./types").BusinessCenterWorkspaceRecord[];
    templates: import("./types").BusinessCenterWorkspaceTemplateRecord[];
    dashboard: import("./types").BusinessCenterWorkspaceDashboard;
    statuses: Array<{
      id: string;
      status: import("./types").BusinessCenterWorkspaceStatus;
      provisioningStatus: import("./types").BusinessCenterWorkspaceProvisioningStatus;
      portalStatus: import("./types").BusinessCenterWorkspaceRecord["portalStatus"];
    }>;
  }>("/api/admin/business-center/workspaces");
}

export async function getBusinessCenterWorkspace(workspaceId: string) {
  return requestJson<{ workspace: import("./types").BusinessCenterWorkspaceRecord }>(`/api/admin/business-center/workspaces/${workspaceId}`);
}

export async function bulkImportBusinessCenterWorkspaces(workspaces: import("./types").BusinessCenterWorkspaceRecord[]) {
  return requestJson<{
    workspaces: import("./types").BusinessCenterWorkspaceRecord[];
    dashboard: import("./types").BusinessCenterWorkspaceDashboard;
  }>("/api/admin/business-center/workspaces", {
    method: "POST",
    body: JSON.stringify({ workspaces }),
  });
}

export async function upsertBusinessCenterWorkspace(workspace: import("./types").BusinessCenterWorkspaceRecord) {
  return requestJson<{
    workspace: import("./types").BusinessCenterWorkspaceRecord;
    dashboard: import("./types").BusinessCenterWorkspaceDashboard;
  }>("/api/admin/business-center/workspaces", {
    method: "POST",
    body: JSON.stringify({ workspace }),
  });
}

export async function updateBusinessCenterWorkspace(
  workspaceId: string,
  payload: {
    action?: "activate" | "pause" | "archive" | "provision" | "switch";
    status?: import("./types").BusinessCenterWorkspaceStatus;
    note?: string;
    changes?: Record<string, unknown>;
  },
) {
  return requestJson<{
    workspace: import("./types").BusinessCenterWorkspaceRecord;
    dashboard: import("./types").BusinessCenterWorkspaceDashboard;
  }>(`/api/admin/business-center/workspaces/${workspaceId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function inviteBusinessCenterUser(payload: {
  user: BusinessCenterUserRecord;
  companyName: string;
  invitationMessage?: string;
}) {
  return requestJson<{
    user: BusinessCenterUserRecord;
    users: BusinessCenterUserRecord[];
    invitationStatus: string;
    deliveryStatus: string;
    deliveryMessageId?: string;
    expiresAt: string;
  }>("/api/admin/business-center/invitations", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export interface BusinessCenterUserDeletionDependency {
  moduleKey: string;
  scopeKey: string;
  count: number;
  reason: string;
}

export interface BusinessCenterPermanentUserDeletionResult {
  eligible: boolean;
  deleted?: boolean;
  userId: string;
  displayName: string;
  email: string;
  dependencies: BusinessCenterUserDeletionDependency[];
  cleanup: {
    canonicalUsersRemoved: number;
    platformUsersRemoved: number;
    invitationsRemoved: number;
    teamAssignmentsRemoved: number;
    sessionsRevoked: number;
    accessCodesInvalidated: number;
    loginUsersRemoved: number;
  };
  users?: BusinessCenterUserRecord[];
  platformUsers?: Array<{
    id: string;
    displayName: string;
    email: string;
    role: string;
    status: string;
    team: string;
    lastLogin: string;
    lastLoginAt?: string;
    invitationStatus: string;
    defaultLandingWorkspace: string;
  }>;
  message: string;
}

export async function assessPermanentBusinessCenterUserDeletion(userId: string) {
  return requestJson<BusinessCenterPermanentUserDeletionResult>("/api/admin/business-center/users/permanent-delete", {
    method: "POST",
    body: JSON.stringify({ userId, dryRun: true }),
  });
}

export async function permanentlyDeleteBusinessCenterUser(payload: { userId: string; confirmationEmail: string }) {
  return requestJson<BusinessCenterPermanentUserDeletionResult>("/api/admin/business-center/users/permanent-delete", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getBusinessCenterInvitation(token: string) {
  return requestJson<{
    invitation: {
      fullName: string;
      role: string;
      companyId: string;
      team?: string;
      defaultLandingWorkspace?: string;
      invitationMessage: string;
      invitationStatus: string;
      expiresAt: string;
    };
  }>(`/api/business-center/invitation/${encodeURIComponent(token)}`);
}

export async function acceptBusinessCenterInvitation(
  token: string,
  payload: { email: string; code?: string; action: "prepare_login" | "complete" },
) {
  return requestJson<{
    user: BusinessCenterUserRecord;
    users: BusinessCenterUserRecord[];
    invitationStatus: string;
    message?: string;
    devCode?: string;
    requestedAt?: string;
    expiresAt?: string;
    deliveryStatus?: string;
    deliveryMessageId?: string;
    lastLoginAt?: string;
  }>(`/api/business-center/invitation/${encodeURIComponent(token)}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function createAdminClientUser(payload: {
  clientId: string;
  email: string;
  name: string;
  role?: "client" | "viewer";
  loginNotes?: string;
}) {
  return requestJson<{ user: unknown }>("/api/admin/client-users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function resetAdminClientAccess(payload: { userId?: string; email?: string }) {
  return requestJson<{ ok: true; message: string; userId: string; email: string }>("/api/admin/reset-client-access", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function createAdminVault(payload: {
  clientId: string;
  eventName: string;
  eventSlug?: string;
  vaultDisplayName?: string;
  eventType?: string;
  eventDate?: string;
  coverImage?: string;
  optionalLogo?: string;
  primaryThemeColor?: string;
  secondaryThemeColor?: string;
  accentColor?: string;
  welcomeMessage?: string;
  guestUploadInstructions?: string;
  uploadButtonText?: string;
  confirmationMessage?: string;
  approvalNotice?: string;
}) {
  return requestJson<{
    event: EventRecord;
    vault: unknown;
    vaultEvent: unknown;
    guestUploadLink: { id: string; upload_slug?: string; share_url?: string; share_url_placeholder?: string };
  }>("/api/admin/vaults", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function generateAdminGuestUploadLink(payload: {
  clientId?: string;
  eventId?: string;
  guestUploadLinkId?: string;
  regenerate?: boolean;
}) {
  return requestJson<{ link: { id: string; upload_slug?: string; share_url?: string; share_url_placeholder?: string } }>(
    "/api/admin/guest-upload-links",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export async function login(email: string, admin = false) {
  return requestJson<{
    message: string;
    devCode?: string;
    requestedAt?: string;
    expiresAt?: string;
    deliveryStatus?: string;
    deliveryProvider?: string;
    deliveryMessageId?: string;
    helpText?: string;
    resendAvailableAt?: string;
  }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, admin }),
  });
}

export async function verifyLogin(email: string, code: string, admin = false) {
  return requestJson<{ user: { email: string; role: string }; recoveryCodeUsed?: boolean }>("/api/auth/verify", {
    method: "POST",
    body: JSON.stringify({ email, code, admin }),
  });
}

export async function getSession() {
  return requestJson<{ user: { email: string; role: string } | null }>("/api/auth/session");
}

export async function logout() {
  return requestJson<{ ok: true }>("/api/auth/logout", { method: "POST" });
}
